import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface PricingConfig {
  id: string;
  duty_percentage: number;
  vat_percentage: number;
}

export const PricingConfigEditor = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [dutyPercentage, setDutyPercentage] = useState('');
  const [vatPercentage, setVatPercentage] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    const { data, error } = await supabase
      .from('pricing_config')
      .select('*')
      .single();

    if (!error && data) {
      setConfig(data);
      setDutyPercentage(data.duty_percentage.toString());
      setVatPercentage(data.vat_percentage.toString());
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!config) return;

    setLoading(true);

    const { error } = await supabase
      .from('pricing_config')
      .update({
        duty_percentage: parseFloat(dutyPercentage),
        vat_percentage: parseFloat(vatPercentage)
      })
      .eq('id', config.id);

    setLoading(false);

    if (error) {
      toast.error('Failed to update pricing config');
    } else {
      toast.success('Pricing config updated');
      fetchConfig();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing Configuration</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="duty">Duty Percentage (%)</Label>
            <Input
              id="duty"
              type="number"
              step="0.01"
              value={dutyPercentage}
              onChange={(e) => setDutyPercentage(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat">VAT Percentage (%)</Label>
            <Input
              id="vat"
              type="number"
              step="0.01"
              value={vatPercentage}
              onChange={(e) => setVatPercentage(e.target.value)}
              required
            />
          </div>

          <Button type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};